import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Seo } from '../components/Seo';
import { articleSchema, breadcrumb, faqPageSchema } from '../lib/structuredData';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Plane, Hotel, Ticket, MapPin, Clock, CalendarDays, ChevronRight, Home as HomeIcon,
  Car, Utensils, Sparkles, Landmark, Wine, Waves, Bike, Music, Mountain, Flame, Leaf,
  ExternalLink, Lightbulb, HelpCircle, Tag, ArrowRight, TrainFront, Bus, Swords,
  FerrisWheel, Blocks, Fish, Zap, Flag, Moon, Building2, Droplets, Ship, Gamepad2, Trees,
  Map as MapIcon, Heart, Share2, MessageCircle,
} from 'lucide-react';

const KIND_ICON = {
  car: Car, food: Utensils, spa: Sparkles, temple: Landmark, wine: Wine,
  swim: Waves, bike: Bike, music: Music, waves: Waves, mountain: Mountain,
  flame: Flame, leaf: Leaf, ticket: Ticket,
  rail: TrainFront, transfer: Bus, landmark: Landmark, sumo: Swords, kimono: Sparkles,
  theme: FerrisWheel, lego: Blocks, aqua: Fish, pass: Ticket, ghibli: Trees, express: Zap,
  hotel: Hotel, kart: Flag, carculture: Moon, jdm: Car, museum: Building2,
  onsen: Droplets, boat: Ship, game: Gamepad2,
};

const ActivityCard = ({ a }) => {
  const Ic = KIND_ICON[a.kind] || Ticket;
  const links = a.providerLinks || (a.url ? [{ provider: a.provider, url: a.url, price: a.priceUsd }] : []);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col sm:flex-row" data-testid={`activity-${a.id}`}>
      {a.image ? (
        <div className="sm:w-40 h-32 sm:h-auto shrink-0 overflow-hidden bg-secondary">
          <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="sm:w-40 h-24 sm:h-auto shrink-0 grid place-items-center bg-accent/10 text-accent">
          <Ic className="h-8 w-8" />
        </div>
      )}
      <div className="p-4 flex flex-col gap-2 flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className="grid place-items-center h-7 w-7 rounded-lg bg-accent/10 text-accent shrink-0"><Ic className="h-4 w-4" /></span>
          <h3 className="text-sm font-semibold leading-snug">{a.title}</h3>
        </div>
        <div className="flex flex-wrap gap-2 mt-auto pt-1">
          {links.map((lk, i) => (
            <a key={i} href={lk.url} target="_blank" rel="sponsored noopener noreferrer" className="inline-flex" data-testid={`book-${a.id}-${i}`}>
              <Button size="sm" className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90">
                Book on {lk.provider}{lk.price != null ? ` · from $${Number(lk.price).toFixed(2)}` : ''}
                <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function EditorialItinerary() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [it, setIt] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const [related, setRelated] = React.useState([]);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    api.editorialBySlug(slug)
      .then(({ data }) => { if (!alive) return; setIt(data.itinerary); setSaved(!!data.itinerary.saved); })
      .catch(() => alive && setNotFound(true));
    return () => { alive = false; };
  }, [slug]);

  React.useEffect(() => {
    if (!it) return;
    api.editorialList({ group: it.group }).then(({ data }) =>
      setRelated((data.itineraries || []).filter((r) => r.slug !== it.slug))).catch(() => {});
  }, [it]);

  React.useEffect(() => { if (notFound) navigate('/itineraries', { replace: true }); }, [notFound, navigate]);

  const onSave = async () => {
    try { const { data } = await api.editorialSave(slug); setSaved(data.saved); toast.success(data.saved ? 'Saved to your account' : 'Removed from saved'); }
    catch { toast.error('Please sign in to save this guide'); }
  };
  const onShare = async () => {
    const url = `${window.location.origin}/api/share/itinerary/${slug}`;
    try {
      if (navigator.share) { await navigator.share({ title: it.h1, text: it.metaDescription, url }); }
      else { await navigator.clipboard.writeText(url); toast.success('Link copied — share it with your trip buddy!'); }
    } catch { /* user cancelled */ }
  };
  const onWhatsApp = () => {
    const url = `${window.location.origin}/api/share/itinerary/${slug}`;
    const msg = `Check out this ${it.destinationShort || it.destination} travel guide from FlightDeals.in — ${it.h1}: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  if (!it) return <Layout><div className="max-w-4xl mx-auto px-4 py-20 text-muted-foreground">Loading…</div></Layout>;

  const path = `/itineraries/${it.slug}`;
  const dShort = it.destinationShort || it.destination;
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const ogImage = `${BACKEND}/api/og/editorial/${it.slug}.png`;
  const flightsHref = `/flights/results?origin=CCU&destination=${it.destinationCode}&adults=1&cabin=economy`;
  const firstVal = it.days?.[0]?.key || 'day-1';
  const jsonLd = [
    articleSchema({ headline: it.h1, description: it.metaDescription, image: it.heroImage, path, authorName: 'FlightDeals.in', authorPath: '/', datePublished: it.published_at || '2026-01-01' }),
    breadcrumb([{ name: 'Home', path: '/' }, { name: 'Itineraries', path: '/itineraries' }, { name: it.h1 }]),
    faqPageSchema(it.faqs || []),
  ];

  return (
    <Layout>
      <Seo title={it.metaTitle} description={it.metaDescription} path={path} image={ogImage} type="article" jsonLd={jsonLd} />

      <div data-testid="editorial-itinerary-page">
        <section className="relative h-[360px] sm:h-[420px]">
          <img src={it.heroImage} alt={it.heroAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-primary-foreground">
              <nav className="flex items-center gap-1 text-xs text-primary-foreground/70 mb-2">
                <Link to="/" className="hover:text-accent flex items-center gap-1"><HomeIcon className="h-3 w-3" /> Home</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to="/itineraries" className="hover:text-accent">Itineraries</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-primary-foreground">{it.destination}</span>
              </nav>
              {it.badge && <Badge className="bg-accent text-accent-foreground rounded-full mb-2">{it.badge}</Badge>}
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-3xl">{it.h1}</h1>
            </div>
          </div>
        </section>

        {/* Sticky bar: flights + save + share */}
        <div className="sticky top-0 z-40 bg-primary/95 backdrop-blur border-b border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2">
            <Button onClick={onSave} variant="outline" size="sm" className={`rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 ${saved ? 'text-accent' : ''}`} data-testid="save-guide-button">
              <Heart className={`h-4 w-4 mr-1.5 ${saved ? 'fill-accent text-accent' : ''}`} /> {saved ? 'Saved' : 'Save'}
            </Button>
            <Button onClick={onShare} variant="outline" size="sm" className="rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20" data-testid="share-guide-button">
              <Share2 className="h-4 w-4 mr-1.5" /> Share
            </Button>
            <Button onClick={onWhatsApp} size="sm" className="rounded-xl bg-[#25D366] text-white hover:bg-[#1ebe5b] font-semibold" data-testid="whatsapp-share-button">
              <MessageCircle className="h-4 w-4 mr-1.5 fill-white" /> WhatsApp
            </Button>
            <Link to={flightsHref} className="ml-auto" data-testid="sticky-find-flights">
              <Button size="sm" className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
                <Plane className="h-4 w-4 mr-1.5" /> Find Flights to {dShort}
              </Button>
            </Link>
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[[MapPin, 'Destination', it.destination], [Clock, 'Duration', `${it.durationDays} days`], [CalendarDays, 'Best time', it.bestTime]].map(([Ic, l, v], i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Ic className="h-3.5 w-3.5 text-accent" /> {l}</div>
                <p className="font-semibold text-sm mt-0.5 truncate">{v || '—'}</p>
              </div>
            ))}
          </div>

          <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>

          {it.intro && <p className="text-base text-muted-foreground leading-relaxed mb-6">{it.intro}</p>}

          <Accordion type="multiple" defaultValue={[firstVal]} className="space-y-3" data-testid="itinerary-days">
            {(it.days || []).map((day, di) => {
              const val = day.key || `day-${di + 1}`;
              return (
                <AccordionItem key={val} value={val} className="rounded-2xl border border-border bg-card px-4 border-b-0">
                  <AccordionTrigger className="hover:no-underline" data-testid={`day-trigger-${val}`}>
                    <span className="flex items-center gap-3 text-left">
                      <span className="grid place-items-center h-9 min-w-9 px-2 rounded-xl bg-accent/10 text-accent font-semibold shrink-0 text-sm">{day.badge || di + 1}</span>
                      <h2 className="font-display font-semibold text-lg">{day.heading}</h2>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      {(day.body || []).map((p, i) => <p key={i} className="text-sm text-muted-foreground leading-relaxed">{p}</p>)}
                      {day.images?.length > 0 && (
                        <div className={`grid gap-3 ${day.images.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                          {day.images.map((img, i) => <img key={i} src={img.src} alt={img.alt} loading="lazy" className="w-full h-56 object-cover rounded-xl" />)}
                        </div>
                      )}
                      {day.activities?.length > 0 && (
                        <div className="grid gap-3">{day.activities.map((a) => <ActivityCard key={a.id} a={a} />)}</div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {it.tipBox && it.tipBox.title && (
            <div className="mt-6 rounded-2xl border border-accent/30 bg-accent/8 p-5" data-testid="itinerary-tip-box">
              <div className="flex items-center gap-2 mb-2 text-accent font-semibold"><Lightbulb className="h-4 w-4" /> {it.tipBox.title}</div>
              <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-5">
                {(it.tipBox.items || []).map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              {it.tipBox.cta && it.tipBox.cta.url && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <a href={it.tipBox.cta.url} target="_blank" rel="sponsored noopener noreferrer" data-testid="book-activity-tipcta">
                    <Button size="sm" variant="outline" className="rounded-lg"><Car className="h-3.5 w-3.5 mr-1.5" /> {it.tipBox.cta.title}{it.tipBox.cta.price ? ` · from $${Number(it.tipBox.cta.price).toFixed(2)}` : ''} <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></Button>
                  </a>
                </div>
              )}
            </div>
          )}

          {it.localTips?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-border bg-secondary/40 p-5" data-testid="itinerary-local-tips">
              <div className="flex items-center gap-2 mb-2 font-semibold"><MapIcon className="h-4 w-4 text-accent" /> Local recommendations</div>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {it.localTips.map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                    <span>{t.text}{t.mapQuery && (<> — <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t.mapQuery)}`} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline" data-testid={`map-link-${i}`}>View on map</a></>)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 rounded-2xl bg-primary text-primary-foreground p-6">
            <h2 className="font-display font-semibold text-xl mb-3">Complete your {dShort} trip</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Link to={flightsHref}><Button className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl" data-testid="complete-flights"><Plane className="h-4 w-4 mr-1.5" /> Flights to {dShort}</Button></Link>
              <Link to={`/hotels?destination_code=${it.destinationCode}`}><Button variant="outline" className="w-full rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20"><Hotel className="h-4 w-4 mr-1.5" /> Hotels</Button></Link>
              <Link to={`/experiences?destination_code=${it.destinationCode}`}><Button variant="outline" className="w-full rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20"><Ticket className="h-4 w-4 mr-1.5" /> Experiences</Button></Link>
              <Link to="/deals"><Button variant="outline" className="w-full rounded-xl bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20" data-testid="complete-deals"><Tag className="h-4 w-4 mr-1.5" /> {dShort} Deals</Button></Link>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-8" data-testid="itinerary-related">
              <h2 className="font-display font-semibold text-xl mb-3">Looking for a different kind of {dShort} trip?</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link key={r.slug} to={`/itineraries/${r.slug}`} className="group rounded-2xl border border-border bg-card overflow-hidden card-elevate flex" data-testid={`related-${r.slug}`}>
                    <div className="w-28 shrink-0 overflow-hidden"><img src={r.cover} alt={r.coverAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                    <div className="p-3 flex flex-col min-w-0">
                      <Badge className="w-fit bg-accent/10 text-accent rounded-full text-[10px] mb-1">{r.badge}</Badge>
                      <p className="text-sm font-semibold leading-snug line-clamp-2">{r.title}</p>
                      <span className="mt-auto pt-2 text-accent text-xs font-semibold inline-flex items-center gap-1">Read guide <ArrowRight className="h-3.5 w-3.5" /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {it.faqs?.length > 0 && (
            <div className="mt-8" data-testid="itinerary-faq">
              <h2 className="font-display font-semibold text-xl mb-3 flex items-center gap-2"><HelpCircle className="h-5 w-5 text-accent" /> Frequently asked questions</h2>
              <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-4">
                {it.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className={i === it.faqs.length - 1 ? 'border-b-0' : ''}>
                    <AccordionTrigger className="hover:no-underline text-sm font-medium" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
                    <AccordionContent><p className="text-sm text-muted-foreground">{f.a}</p></AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
